import { eventSystem } from '@business/event-system';
import { FeatureFlag, FeatureFlagManager } from '@business/feature-flags';
import Store from '@data/stores/Store';
function getAccommodationsFromStore() {
    return Store.property.accommodations.model.items;
}
function getSplitInventory() {
    return Store.property.accommodations.model.splitInventory;
}
function getIsBookableLimitEnabled() {
    return Store.property.accommodations.model.bookableLimitEnabled;
}
function compareItems(isSelectSpecificRoom, a, b) {
    if (isSelectSpecificRoom) {
        return !!a.unitId && !!b.unitId && a.unitId === b.unitId;
    }
    return a.roomId === b.roomId;
}
class Inventory {
    constructor() {
        Object.defineProperty(this, "accommodations", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: []
        });
        Object.defineProperty(this, "accommodationsMap", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: {}
        });
        Object.defineProperty(this, "accommodationsMapByPackage", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: {}
        });
        Object.defineProperty(this, "availableItems", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: []
        });
        Object.defineProperty(this, "blockedItems", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: []
        });
        Object.defineProperty(this, "cartItems", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: []
        });
        Object.defineProperty(this, "isBookableLimitEnabled", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: false
        });
        Object.defineProperty(this, "splitInventory", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: []
        });
    }
    /**
     * Blocks children items for each unit in the cart.
     * For each unit of a root item added to cart, blocks all its required sub-units.
     */
    blockChildrenForCartItem(blockedItems) {
        if (blockedItems.length === 0) {
            return;
        }
        for (const cartItem of blockedItems) {
            // Find all split inventory entries that match this cart item
            const rootItem = this.splitInventory.find((item) => item.roomId === cartItem.roomId && item.unitId === cartItem.unitId);
            const children = (rootItem === null || rootItem === void 0 ? void 0 : rootItem.children) || [];
            children.forEach((child) => {
                this.blockItem(Object.assign(Object.assign({}, child), { reason: 'root-blocked', selectSpecificRoom: true }));
            });
        }
    }
    /**
     * Blocks a specific quantity of items from available inventory.
     * Uses iteration instead of recursion for better performance.
     */
    blockItem({ roomId, unitId, reason, quantity = 1, selectSpecificRoom = false, }) {
        const canSelectSpecificRoom = this.isSpecificSelection({ roomId });
        const isSpecificRoom = selectSpecificRoom || canSelectSpecificRoom;
        const res = [];
        for (let i = 0; i < quantity; i++) {
            const index = this.availableItems.findIndex((availableItem) => compareItems(isSpecificRoom, availableItem, { roomId, unitId }));
            if (index !== -1) {
                const blocked = this.availableItems.splice(index, 1);
                const blockedItems = blocked.map((item) => (Object.assign(Object.assign({}, item), { reason })));
                this.blockedItems.push(...blockedItems);
                res.push(...blockedItems);
            }
        }
        return res;
    }
    /**
     * Blocks items from shopping cart dynamically based on what's actually in the cart.
     * Only blocks the specific items/units that were added, not entire split inventory groups.
     */
    blockShoppingCartItems({ blockNonRootSplitInventory = true, blockRootSplitInventory = false, }) {
        this.cartItems.forEach((cartItem) => {
            const isRootSplitInventory = this.isRootSplitInventoryItem({
                roomId: cartItem.item.id,
            });
            const earlyExit = (isRootSplitInventory && !blockRootSplitInventory) ||
                (!isRootSplitInventory && !blockNonRootSplitInventory);
            if (earlyExit) {
                return;
            }
            // Always block the cart item itself
            const blockedItems = this.blockItem({
                quantity: cartItem.amount,
                reason: 'in-cart',
                roomId: cartItem.item.id,
                unitId: cartItem.unit,
            });
            // Additionally block children if this is a root split inventory item
            if (this.isRootSplitInventoryItem({ roomId: cartItem.item.id })) {
                this.blockChildrenForCartItem(blockedItems);
            }
        });
    }
    /**
     * Blocks root split inventory items if any of their required children are unavailable.
     * This ensures that a parent item can't be selected if its dependencies aren't available.
     */
    blockUnavailableSplitInventoryRoots() {
        // Only check root items that are still available
        const notBlockedRootItems = this.splitInventory.filter((item) => !this.isItemBlocked(item, 'any-reason'));
        for (const rootItem of notBlockedRootItems) {
            // Check if any required children are not available (either blocked or don't exist in inventory)
            const hasUnavailableChildren = rootItem.children.some((child) => this.isItemBlocked(child, 'any-reason'));
            // If any child is unavailable, block this specific root item
            if (hasUnavailableChildren) {
                this.blockItem(Object.assign(Object.assign({}, rootItem), { reason: 'unavailable-children', selectSpecificRoom: true }));
            }
        }
    }
    getAccommodation({ roomId }) {
        return this.accommodationsMap[roomId];
    }
    /**
     * Helper to determine available count for an accommodation.
     */
    getAccommodationAvailableCount(accommodation) {
        var _a;
        return (((_a = accommodation.accommodations) === null || _a === void 0 ? void 0 : _a.length) || accommodation.numAvailable || 0);
    }
    getAccommodationFromPackageId({ packageId, roomId, }) {
        return this.accommodationsMapByPackage[roomId + '-' + packageId];
    }
    /**
     * Gets unit IDs for an accommodation, considering split inventory structure.
     * Returns units based on accommodation configuration and children availability.
     */
    getAccommodationUnits(accommodation) {
        const sortedUnits = this.getBaseAccommodationUnits(accommodation);
        const rootItems = [];
        const childItems = [];
        const nonSplitItems = [];
        for (const unit of sortedUnits) {
            const isRoot = this.isRootSplitInventoryItem({
                roomId: accommodation.id,
                unitId: unit,
            });
            const isChild = this.isChildSplitInventoryItem({
                roomId: accommodation.id,
                unitId: unit,
            });
            if (!isRoot && !isChild) {
                nonSplitItems.push(unit);
            }
            else if (isRoot) {
                rootItems.push(unit);
            }
            else if (isChild) {
                childItems.push(unit);
            }
        }
        const limit = this.getAccommodationAvailableCount(accommodation);
        return [...nonSplitItems, ...childItems, ...rootItems].slice(0, limit);
    }
    /**
     * Gets base unit IDs for an accommodation without checking split inventory children.
     * This is used to determine which units are configured/available for a specific accommodation.
     */
    getBaseAccommodationUnits(accommodation) {
        var _a;
        // Safety: ensure array exists
        const subAccommodations = Array.isArray(accommodation.accommodations)
            ? accommodation.accommodations
            : [];
        // Individual selectable accommodations use their own unit ids
        if (subAccommodations.length > 0) {
            return this.sortUnitIds(subAccommodations
                .map((a) => a === null || a === void 0 ? void 0 : a.unitId)
                .filter((id) => typeof id === 'string'));
        }
        const baseUnitIds = Array.isArray(accommodation.unit_ids)
            ? accommodation.unit_ids
            : [];
        let allSubUnits = [...baseUnitIds];
        // If no unit ids defined, generate them based on numAvailable
        const numAvailable = (_a = accommodation.numAvailable) !== null && _a !== void 0 ? _a : 0;
        if (allSubUnits.length === 0) {
            allSubUnits = Array.from({ length: numAvailable }, (_, i) => {
                var _a;
                const accId = (_a = accommodation.id) !== null && _a !== void 0 ? _a : 'acc';
                return `${accId}-${i}`;
            });
        }
        return this.sortUnitIds(allSubUnits);
    }
    getBookableLimit({ packageId, roomId, }) {
        const accommodation = this.getAccommodationFromPackageId({
            packageId,
            roomId,
        });
        if (!accommodation)
            return 0;
        const accommodationNumAvailable = this.getAccommodationAvailableCount(accommodation);
        if (!this.isBookableLimitEnabled)
            return accommodationNumAvailable;
        return Math.min(accommodationNumAvailable, accommodation.sourceSettings.bookableLimit);
    }
    getInventoryItems() {
        const items = [];
        // Multiple packages for the same room type produce separate Accommodation
        // entries that share the same roomId and unitIds. Without deduplication,
        // availableItems would contain N copies of each unit (one per package),
        // causing blockItem to remove only one copy while the rest keep the
        // accommodation appearing as available.
        const seen = new Set();
        for (const accommodation of this.accommodations) {
            const units = this.getAccommodationUnits(accommodation);
            for (let i = 0; i < units.length; i++) {
                const key = `${accommodation.id}:${units[i]}`;
                if (seen.has(key)) {
                    continue;
                }
                seen.add(key);
                items.push({ roomId: accommodation.id, unitId: units[i] });
            }
        }
        return items;
    }
    /**
     * Returns the total quantity of a specific item/unit in the shopping cart.
     */
    getNumAddedToCart({ roomId, unitId }) {
        return this.cartItems
            .filter(({ item }) => compareItems(!!unitId, { roomId: item.id }, { roomId, unitId }))
            .reduce((total, cartItem) => total + cartItem.amount, 0);
    }
    /**
     * Calculates how many units of a specific item are available to book.
     * Takes into account current availability, bookable limits, and cart contents.
     */
    getNumAvailableToBook({ packageId, roomId, unitId, }) {
        const canSelectSpecificRoom = this.isSpecificSelection({ roomId });
        const numAvailable = this.availableItems.filter((item) => compareItems(canSelectSpecificRoom, item, { roomId, unitId })).length;
        const bookableLimit = this.getBookableLimit({ packageId, roomId });
        const numAddedToCart = this.getNumAddedToCart({ roomId });
        return Math.min(numAvailable, bookableLimit - numAddedToCart);
    }
    isChildSplitInventoryItem({ roomId, unitId, }) {
        for (const rootItem of this.splitInventory) {
            if (rootItem.children.some((child) => child.roomId === roomId && (!unitId || child.unitId === unitId))) {
                return true;
            }
        }
        return false;
    }
    isItemAvailable(item) {
        return this.availableItems.some((availableItem) => compareItems(true, availableItem, item));
    }
    isItemBlocked(item, reason) {
        return this.blockedItems.some((blockedItem) => (reason === 'any-reason' || blockedItem.reason === reason) &&
            compareItems(true, blockedItem, item));
    }
    isRootSplitInventoryItem({ roomId, unitId }) {
        return this.splitInventory.some((item) => item.roomId === roomId && (!unitId || item.unitId === unitId));
    }
    isSpecificSelection({ roomId }) {
        const accommodation = this.getAccommodation({ roomId });
        if (!accommodation)
            return false;
        return accommodation.sourceSettings.canSelectSpecificRoom;
    }
    recalculate({ cartItems = [], } = {}) {
        // If the flag to use the new split inventory system is off, do nothing
        if (!FeatureFlagManager.isEnabled(FeatureFlag.useNewSplitInventorySystem)) {
            return;
        }
        this.cartItems = [...cartItems];
        this.accommodations = getAccommodationsFromStore();
        this.accommodationsMap = Object.fromEntries(this.accommodations.map((item) => [item.id, item]));
        this.accommodationsMapByPackage = Object.fromEntries(this.accommodations.map((item) => [item.id + '-' + item.packageId, item]));
        this.splitInventory = getSplitInventory();
        this.availableItems = this.getInventoryItems();
        this.blockedItems = [];
        this.isBookableLimitEnabled = getIsBookableLimitEnabled();
        // Block items in shopping cart in specific order (critical for correct behavior):
        // 1. First block non-root cart items (individual items)
        // 2. Block root items whose children are unavailable due to step 1
        // 3. Block root cart items and their children
        // 4. Block root items whose children are unavailable due to step 3
        this.blockShoppingCartItems({
            blockNonRootSplitInventory: true,
            blockRootSplitInventory: false,
        });
        this.blockUnavailableSplitInventoryRoots();
        this.blockShoppingCartItems({
            blockNonRootSplitInventory: false,
            blockRootSplitInventory: true,
        });
        this.blockUnavailableSplitInventoryRoots();
        eventSystem.emit('internal/inventory-recalculated', undefined, {
            visibility: 'internal',
        });
    }
    /**
     * Sorts unit IDs alphanumerically, handling both simple IDs and hyphenated formats.
     * Examples: "101", "102", "101-1", "101-2"
     */
    sortUnitIds(unitIds) {
        return [...unitIds].sort((a, b) => {
            const [aPrefix, aSuffix = '0'] = a.split('-');
            const [bPrefix, bSuffix = '0'] = b.split('-');
            // Compare prefixes first
            const prefixDiff = (Number(aPrefix) || 0) - (Number(bPrefix) || 0);
            if (prefixDiff !== 0)
                return prefixDiff;
            // If prefixes are equal, compare suffixes
            return (Number(aSuffix) || 0) - (Number(bSuffix) || 0);
        });
    }
}
export const inventory = new Inventory();
